<style>
.institutionLogo {
  border: 1px solid #e3e3e1;
  height: 50px !important;
}

.accounts-col {
  border: 1px solid #d9d9d9;
  padding-left: 0;
  padding-right: 0
}

.config-col {
  align-content: center;
  text-align: center;
}

.linked-accounts {
  border-bottom: 1px solid #d9d9d9;
  width: 100%;
  margin-left: 0;
  margin-right: 0;
  text-align: center;
}
</style>
<template>
  <title-container :title="pageTitle" icon="building">
    <b-row>
      <div class="col-12 mr-3 ml-3">
        <p>
          Instead of having to submit your individual bank statements to Anchor for your
          loans for your proof of funds, you now have the option to allow Anchor to
          request statements from the Bank(s) and Account(s) of your choice directly.
          This is done through the <a href="https://plaid.com/" target="_blank">PLAID</a>
           platform, which is used by millions of users and is certified in
           internationally-recognized security standards, like ISO 27001,
          ISO 27701, and is SSAE18 SOC 2 compliant. <br/>
          ** This is ONLY for Proof of Funds verification. This is separate than
          ACH payment accounts and Anchor can NOT draw funds through this service.
        </p>
      </div>
      <b-col xl="6" class="accounts-col mb-2">
        <div class="linked-accounts">
          Current Linked Accounts
        </div>
        <div
          v-for="asset_report in plaidItemList"
          v-bind:key="asset_report.asset_report_id"
          v-if="!fetchPlaidItemsError"
        >
          <a
            v-if="checkLocked(asset_report['items'][0]['item_id'])"
            @click="openItemUpdateCredentialsModal(asset_report)"
          >
            <item-card
              :record="asset_report"
              :locked="true"
              class="ml-4 pl-2 pt-2"
            />
          </a>
          <a @click="openAccountsModal(asset_report)" v-else>
            <item-card
              :record="asset_report"
              class="ml-4 pl-2 pt-2"
            />
          </a>
        </div>
      </b-col>
      <b-col xl="6" class="config-col">
        <div>
          <plaid-link
            v-if="token"
            :env="`${environment}`"
            clientName="Borrower Portal"
            product="assets"
            v-bind="{ onSuccess, onLoad }"
            :token="token"
          >
            <template slot="button" slot-scope="props">
              <a
                @click="props.onClick"
                class="pointer-cursor btn btn-anchor"
              >
                <font-awesome-icon icon="plus-square"/>
                Connect New Account
              </a>
            </template>
          </plaid-link>
          <b-row
            v-else-if="loadingLinkToken"
            v-show="isPersonalInformationComplete"
          >
            <b-col>
              <font-awesome-icon icon="spinner"/>
              Loading Plaid...
            </b-col>
          </b-row>
          <b-row v-else-if="tokenError">
            <b-col>
              <explicit-hint
                class="ml-3 error"
                text="Failed to load Plaid Link. Please try again later."
              />
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
    <accounts-modal
      ref="showAccountsModal"
      @item-deleted="onItemDeleted"
      @item-retrieve="getPendingAssetReport"
    />
    <item-update-credentials-modal
      ref="showItemUpdateCredentialsModal"
      @reload="onReloadData"
    />

    <plaid-contact-form
      v-show="!isPersonalInformationComplete"
      ref="personalInformation"
      :model="model"
      @success="onInfoUpdated"
    />
  </title-container>

</template>

<script>
import helper from '@/libs/helper';
import DashboardButton from '@/components/utils/buttons/navigation/DashboardButton';
import ExplicitHint from '@/components/utils/hints/ExplicitHint';
import TitleContainer from '@/components/utils/TitleContainer';
import PlaidContactForm from '@/components/profile/sections/tabs/contact/PlaidContactForm';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import ItemCard from './ItemCard';
import PlaidLink from './PlaidLink';
import AccountsModal from './AccountsModal';
import ItemUpdateCredentialsModal from './ItemUpdateCredentialsModal';

library.add([faSpinner]);


export default {
  components: {
    PlaidContactForm,
    AccountsModal,
    DashboardButton,
    ExplicitHint,
    ItemCard,
    TitleContainer,
    PlaidLink,
    ItemUpdateCredentialsModal,
  },
  data() {
    return {
      model: {},
      token: null,
      tokenError: false,
      fetchPlaidItemsError: false,
      plaidItemList: null,
      plaidItemLockedList: null,
      lockedItemIds: [],
      pendingAssetReports: null,
      loadingLinkToken: true,
    };
  },
  mounted() {
    const profile = this.$store.getters.profile;
    if (profile && profile.contact) {
      this.model = helper.copy(profile.contact);
      if (this.isPersonalInformationComplete) {
        this.fetch();
      }
    }
  },
  computed: {
    user() {
      return this.$store.getters.user;
    },
    environment() {
      if (process.env.NODE_ENV === 'production') {
        return 'production';
      }
      return 'sandbox';
    },
    isPersonalInformationComplete() {
      const firstName = this.$store.getters.profile.contact.firstName;
      const lastName = this.$store.getters.profile.contact.lastName;
      const phones = this.$store.getters.profile.contact.phones;
      return firstName && lastName && phones;
    },
    pageTitle() {
      return 'Proof of Funds Accounts';
    },
  },
  watch: {
    plaidItemList(newValue) {
      if (newValue && newValue.length > 0) {
        this.pendingAssetReports = newValue.filter(
          val => val.items[0].date_last_updated === null,
        );
        if (this.pendingAssetReports.length > 0) {
          this.getPendingAssetReport(this.pendingAssetReports[0]);
        }
      }
    },
    plaidItemLockedList(newValue) {
      if (newValue && newValue.length > 0) {
        newValue.forEach((element) => {
          this.lockedItemIds.push(element.item_id);
        });
      }
    },
  },
  methods: {
    fetch() {
      if (this.isPersonalInformationComplete) {
        this.fetchPlaidItems();
        this.createLinkToken();
        this.fetchPlaidLockedItems();
      }
    },
    fetchPlaidItems() {
      this.$reqs.get(`/users/${this.user.id}/plaid/get_item_list`)
        .then((response) => {
          this.plaidItemList = response.data;
        })
        .catch(() => {
          this.fetchPlaidItemsError = true;
        });
    },
    fetchPlaidLockedItems() {
      this.$reqs.get(`/users/${this.user.id}/plaid/get_locked_items`)
        .then((response) => {
          this.lockedItemIds = [];
          this.plaidItemLockedList = response.data;
        })
        .catch(() => {
          this.fetchPlaidItemsError = true;
        });
    },
    onLoad() {
      // placeholder
    },
    createLinkToken() {
      this.$reqs.get(`/users/${this.user.id}/plaid/create_link_token`)
        .then((response) => {
          this.token = response.data.link_token;
          this.loadingLinkToken = false;
        })
        .catch(() => {
          this.tokenError = true;
          this.loadingLinkToken = false;
        });
    },
    onSuccess(token) {
      this.$reqs.post(`/users/${this.user.id}/plaid/create_access_token`, { public_token: token })
        .then((response) => {
          this.plaidItemList = response.data;
          this.$notify({
            group: 'alert',
            text: 'Success! Plaid is processing your request. Refresh this page in few minutes.',
            type: 'success',
            duration: 4000,
            closeOnClick: true,
          });
        })
        .catch(() => {
        });
    },
    openAccountsModal(assetReport) {
      if (!assetReport.items[0].date_last_updated) { return false; }
      this.$refs.showAccountsModal.show(
        assetReport.asset_report_id, assetReport.items[0], assetReport.items[0].accounts,
      );
      return true;
    },
    openItemUpdateCredentialsModal(assetReport) {
      this.$refs.showItemUpdateCredentialsModal.show(assetReport);
    },
    getPendingAssetReport(assetReport) {
      const persist = !assetReport.items[0].date_last_updated;
      this.$reqs.post(`/users/${this.user.id}/plaid/asset_report`, {
        asset_report_id: assetReport.asset_report_id,
        save_to_contacts: persist,
      }).then(() => {});
    },
    onItemDeleted(response) {
      this.plaidItemList = response.data;
      this.$refs.showAccountsModal.hide();
    },
    onReloadData() {
      this.fetchPlaidItems();
      this.fetchPlaidLockedItems();
    },
    checkLocked(itemId) {
      return this.lockedItemIds.some(element => element === itemId);
    },
    onInfoUpdated() {
      this.fetch();
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/plaid/PlaidPage.vue